import { InversionContainer } from '@/controller/container'
import { InjectionTokens } from '@/controller/tokens'

import { UniqueEntityID } from '@/domain/core'
import { UseCase } from '@/domain/core/UseCase'
import { UserChart } from '@/domain/models/UserChart'
import { Result } from '@/domain/protocols/Result'
import { ConvertedChartData } from '@/domain/protocols/UserChart'

import type { IUserChartRepository } from '@/data/UserChartRepository/IUserChartRepository'

export interface SaveUserChartRequest {
  chartId: string
  userEditedName: string
  previewData: ConvertedChartData
  originalFileName: string
  // Byte size of the imported source file (from `File.size`); optional because a
  // re-save path may not have the original File at hand.
  fileSizeBytes?: number
}

export class SaveUserChartUseCase implements UseCase<SaveUserChartRequest, Promise<Result<void>>> {
  private repository: IUserChartRepository

  constructor(repository?: IUserChartRepository) {
    if (repository) {
      this.repository = repository
      return
    }
    const container = InversionContainer.getInstance().getContainer()
    this.repository = container.get<IUserChartRepository>(InjectionTokens.UserChartRepository)
  }

  async execute(request: SaveUserChartRequest): Promise<Result<void>> {
    try {
      const chart = UserChart.create(
        {
          name: request.userEditedName,
          originalFileName: request.originalFileName,
          addedAt: new Date().toISOString(),
          boundaries: request.previewData.bounds,
          minZoom: request.previewData.minZoom,
          maxZoom: request.previewData.maxZoom,
          fileSizeBytes: request.fileSizeBytes,
          deleted: false,
          synced: false
        },
        new UniqueEntityID(request.chartId)
      )
      return await this.repository.saveChart(chart)
    } catch (error) {
      return Result.fail(error)
    }
  }
}
